import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { BookOpen, PlusCircle, Trash2, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Info } from 'lucide-react'
import { api } from '@/lib/api'
import type {
  HelpArticleAdmin,
  HelpArticlePayload,
  HelpArticleUpdatePayload,
} from '@/types'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/Card'

function getErrorMessage(err: unknown, fallback = '操作失败，请稍后再试'): string {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message
  }
  return fallback
}

const emptyPayload: HelpArticlePayload = {
  category: '',
  categoryName: '',
  title: '',
  content: '',
  keywords: [],
  sortOrder: 0,
  isActive: true,
}

type ToastType = 'success' | 'error' | 'warning' | 'info'
interface ToastState {
  type: ToastType
  title: string
  description?: string
}

interface ConfirmDialogState {
  open: boolean
  title: string
  description: string
  confirmLabel?: string
  tone?: 'danger' | 'default'
  onConfirm: () => void
}

export function HelpCenterPage() {
  const [articles, setArticles] = useState<HelpArticleAdmin[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formState, setFormState] = useState<HelpArticlePayload>(emptyPayload)
  const [keywordsInput, setKeywordsInput] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [toast, setToast] = useState<ToastState | null>(null)
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null)

  const isEditing = useMemo(() => Boolean(editingId), [editingId])

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(null), 4000)
    return () => window.clearTimeout(timer)
  }, [toast])

  const showToast = (type: ToastType, title: string, description?: string) => {
    setToast({ type, title, description })
  }

  const toastThemes: Record<ToastType, { container: string; icon: ReactNode }> = {
    success: {
      container: 'border-green-200 bg-green-50 text-green-800',
      icon: <CheckCircle2 className="h-5 w-5 text-green-600" />,
    },
    error: {
      container: 'border-red-200 bg-red-50 text-red-800',
      icon: <XCircle className="h-5 w-5 text-red-600" />,
    },
    warning: {
      container: 'border-amber-200 bg-amber-50 text-amber-800',
      icon: <AlertTriangle className="h-5 w-5 text-amber-600" />,
    },
    info: {
      container: 'border-blue-200 bg-blue-50 text-blue-800',
      icon: <Info className="h-5 w-5 text-blue-500" />,
    },
  }

  const openConfirmDialog = (dialog: Omit<ConfirmDialogState, 'open'>) => {
    setConfirmDialog({ open: true, ...dialog })
  }

  const loadArticles = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const items = await api.getHelpArticles()
      const sorted = items
        .slice()
        .sort((a, b) => a.category.localeCompare(b.category) || a.sortOrder - b.sortOrder)
      setArticles(sorted)
    } catch (err: unknown) {
      const message = getErrorMessage(err, '加载帮助文章失败')
      setError(message)
      showToast('error', '加载失败', message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadArticles()
  }, [loadArticles])

  const handleInputChange = (
    field: keyof HelpArticlePayload,
    value: string | number | boolean,
  ) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const resetForm = () => {
    setFormState(emptyPayload)
    setKeywordsInput('')
    setEditingId(null)
  }

  const handleEdit = (article: HelpArticleAdmin) => {
    setEditingId(article.id)
    setFormState({
      category: article.category,
      categoryName: article.categoryName,
      title: article.title,
      content: article.content,
      keywords: article.keywords,
      sortOrder: article.sortOrder,
      isActive: article.isActive,
    })
    setKeywordsInput(article.keywords.join(', '))
  }

  const handleDelete = (article: HelpArticleAdmin) => {
    openConfirmDialog({
      title: '确认删除帮助文章',
      description: `确定要删除「${article.title}」吗？此操作无法恢复。`,
      confirmLabel: '删除',
      tone: 'danger',
      onConfirm: async () => {
        try {
          await api.deleteHelpArticle(article.id)
          setArticles((prev) => prev.filter((item) => item.id !== article.id))
          if (editingId === article.id) {
            resetForm()
          }
          showToast('success', '删除成功', '帮助文章已删除。')
        } catch (err: unknown) {
          const message = getErrorMessage(err, '删除失败，请稍后再试')
          setError(message)
          showToast('error', '删除失败', message)
        } finally {
          setConfirmDialog(null)
        }
      },
    })
  }

  const handleToggleActive = async (article: HelpArticleAdmin) => {
    try {
      const updated = await api.updateHelpArticle(article.id, {
        isActive: !article.isActive,
      })
      setArticles((prev) =>
        prev.map((item) => (item.id === article.id ? updated : item)),
      )
      if (editingId === article.id) {
        handleEdit(updated)
      }
      showToast('success', article.isActive ? '已下架文章' : '已发布文章')
    } catch (err: unknown) {
      const message = getErrorMessage(err, '更新失败，请稍后再试')
      setError(message)
      showToast('error', '操作失败', message)
    }
  }

  const handleSubmit = async () => {
    const normalizedKeywords = keywordsInput
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)

    const base = {
      category: formState.category.trim(),
      categoryName: formState.categoryName.trim(),
      title: formState.title.trim(),
      content: formState.content.trim(),
      keywords: normalizedKeywords,
      sortOrder: Number.isFinite(formState.sortOrder)
        ? Number(formState.sortOrder)
        : 0,
      isActive: formState.isActive !== false,
    }

    if (!base.category || !base.categoryName || !base.title || !base.content) {
      const message = '请完整填写分类、分类名称、标题与内容'
      setError(message)
      showToast('warning', '信息不完整', message)
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      if (isEditing && editingId) {
        const updatePayload: HelpArticleUpdatePayload = {
          ...base,
        }
        const updated = await api.updateHelpArticle(editingId, updatePayload)
        setArticles((prev) =>
          prev.map((item) => (item.id === editingId ? updated : item)),
        )
        handleEdit(updated)
        showToast('success', '文章已更新', '帮助文档内容已保存。')
      } else {
        const createPayload: HelpArticlePayload = {
          ...base,
        }
        const created = await api.createHelpArticle(createPayload)
        setArticles((prev) => [...prev, created])
        resetForm()
        showToast('success', '创建成功', '新的帮助文章已添加。')
      }
    } catch (err: unknown) {
      const message = getErrorMessage(err, '保存失败，请稍后再试')
      setError(message)
      showToast('error', '保存失败', message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-8">
      {toast && (
        <div
          className={`fixed top-6 right-6 z-50 w-full max-w-sm rounded-2xl border p-4 shadow-lg transition-all duration-200 ${toastThemes[toast.type].container}`}
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5">{toastThemes[toast.type].icon}</div>
            <div className="flex-1">
              <p className="text-sm font-semibold">{toast.title}</p>
              {toast.description && (
                <p className="mt-1 text-xs opacity-80">{toast.description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setToast(null)}
              aria-label="关闭提示"
              className="text-current"
            >
              <XCircle className="h-4 w-4 opacity-60 hover:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>
      )}

      {confirmDialog?.open && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-3xl border border-purple-100 bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{confirmDialog.title}</h3>
                <p className="mt-1 text-sm text-gray-500">{confirmDialog.description}</p>
              </div>
              <button
                type="button"
                onClick={() => setConfirmDialog(null)}
                aria-label="关闭确认"
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setConfirmDialog(null)}>
                取消
              </Button>
              <Button
                onClick={confirmDialog.onConfirm}
                className={
                  confirmDialog.tone === 'danger'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : undefined
                }
              >
                {confirmDialog.confirmLabel ?? '确认'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-600 via-purple-500 to-pink-500 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-black/5" />
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/20 blur-3xl" />
        <div className="absolute -left-20 -bottom-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="relative space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm">
            <BookOpen className="h-4 w-4" /> 帮助中心管理
          </div>
          <h1 className="text-3xl font-bold lg:text-4xl">维护知识库与操作指南</h1>
          <p className="max-w-3xl text-sm text-white/90 lg:text-base">
            快速新增、编辑与调整帮助文章，确保用户获得准确及时的指引。
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-sm text-gray-600">
          当前共有{' '}
          <span className="font-semibold text-gray-900">{articles.length}</span> 篇文章，
          其中{' '}
          <span className="font-semibold text-emerald-600">
            {articles.filter((item) => item.isActive).length}
          </span>{' '}
          篇已发布。
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            onClick={loadArticles}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            {loading ? '刷新中...' : '刷新列表'}
          </Button>
          <Button
            onClick={() => {
              resetForm()
              showToast('info', '已切换至新增模式', '填写表单即可创建新的帮助文章。')
            }}
            className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
          >
            <PlusCircle className="h-4 w-4" />
            新增文章
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <Card className="border-gray-200 shadow-md">
          <CardHeader className="border-b border-gray-100 bg-gray-50">
            <CardTitle className="text-lg font-semibold text-gray-900">
              帮助文章列表
            </CardTitle>
            <p className="text-sm text-gray-500">
              点击文章可在右侧快速编辑内容或调整发布状态。
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-hidden rounded-b-xl">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      分类
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      标题
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      状态
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      更新时间
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                        正在加载...
                      </td>
                    </tr>
                  ) : articles.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                        暂无帮助文章内容。
                      </td>
                    </tr>
                  ) : (
                    articles.map((article) => (
                      <tr key={article.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="font-medium text-gray-900">{article.categoryName}</div>
                          <div className="text-xs text-gray-500">{article.category}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="font-medium text-gray-900">{article.title}</div>
                          <div className="text-xs text-gray-500 truncate">
                            {article.keywords.join(', ')}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${article.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                          >
                            {article.isActive ? '已发布' : '未发布'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(article.updatedAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(article)}
                            >
                              {article.isActive ? '下架' : '发布'}
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                handleEdit(article)
                                showToast('info', '已加载文章内容', '右侧表单已填充该文章信息。')
                              }}
                            >
                              编辑
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDelete(article)}
                            >
                              <Trash2 className="mr-1 h-4 w-4" />
                              删除
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200 shadow-md">
          <CardHeader className="border-b border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-gray-900">
                  {isEditing ? '编辑帮助文章' : '新增帮助文章'}
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  支持使用基础 Markdown（换行、列表等）提升可读性。
                </p>
              </div>
              {isEditing && (
                <Button variant="ghost" size="sm" onClick={resetForm}>
                  取消编辑
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="mt-6 space-y-4">
            <Input
              label="分类英文标识"
              placeholder="例如 getting-started"
              value={formState.category}
              onChange={(event) => handleInputChange('category', event.target.value)}
            />
            <Input
              label="分类名称"
              placeholder="例如 快速入门"
              value={formState.categoryName}
              onChange={(event) => handleInputChange('categoryName', event.target.value)}
            />
            <Input
              label="文章标题"
              placeholder="请输入标题"
              value={formState.title}
              onChange={(event) => handleInputChange('title', event.target.value)}
            />
            <Textarea
              label="文章内容"
              placeholder="支持换行、列表等基础格式"
              rows={8}
              value={formState.content}
              onChange={(event) => handleInputChange('content', event.target.value)}
            />
            <Input
              label="关键词（逗号分隔）"
              placeholder="搜索关键词，例如 登录, 书架"
              value={keywordsInput}
              onChange={(event) => setKeywordsInput(event.target.value)}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="排序值"
                type="number"
                value={formState.sortOrder ?? 0}
                onChange={(event) =>
                  handleInputChange('sortOrder', Number(event.target.value))
                }
              />
              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-700">发布状态</span>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      checked={formState.isActive !== false}
                      onChange={() => handleInputChange('isActive', true)}
                    />
                    已发布
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      checked={formState.isActive === false}
                      onChange={() => handleInputChange('isActive', false)}
                    />
                    未发布
                  </label>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button variant="secondary" onClick={resetForm} disabled={isSubmitting}>
                重置
              </Button>
              <Button onClick={handleSubmit} loading={isSubmitting}>
                {isEditing ? '保存更新' : '创建文章'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
